/**
 * KYS form PDF (ADR 0033): reproduces the paper form instead of the report layout. Every page carries the
 * controlled-document header (logo | Doküman Kodu | Revizyon No | Rev. Tarih | Sayfa n / N); each filled-in
 * form starts on a new page with its title, the fields, the item table and the signature boxes. A signature
 * box holds a ROLE and a time, never a person's name (owner decision). The footer says the document is
 * controlled under the tenant's KYS and carries the generated-at time and the exporting role (ADR 0031).
 */
import type { Content, TDocumentDefinitions, TableCell } from "pdfmake/interfaces";
import { pdfSafe, toBase64 } from "./pdf";
import { fill, formatDateTimeIn, formatDay, type ExportMeta, type FormDocument, type FormExport } from "./sheet";

const FONT = "NotoSans";
const INK = "#191919";
const MUTED = "#5e6163";
const LINE = "#9a9c9d";
const HEAD_FILL = "#f4f4f2";
const ACCENT = "#f96915";

const MARGIN_X = 32;

type PdfMake = {
  createPdf: (dd: TDocumentDefinitions, tableLayouts?: unknown, fonts?: Record<string, Record<string, string>>, vfs?: Record<string, string>) => { getBuffer: (cb: (buf: Uint8Array) => void) => void };
};
async function loadPdfMake(): Promise<PdfMake> {
  const mod = (await import("pdfmake/build/pdfmake")) as unknown as PdfMake & { default?: PdfMake };
  return mod.default && typeof mod.default.createPdf === "function" ? mod.default : mod;
}

const grid = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => LINE,
  vLineColor: () => LINE,
  paddingTop: () => 3,
  paddingBottom: () => 3,
};

/** The pdfmake definition of a KYS form file (pure: tests read it without building the PDF). */
export function formPdfDefinition(meta: ExportMeta, logo?: string | null, compress = true): TDocumentDefinitions {
  const form = meta.form as FormExport;
  const L = form.labels;
  const content: Content[] = [];
  const s = pdfSafe;

  const day = (d: string | null) => (d ? formatDay(d, meta.locale) : "—");
  /** "Doküman Kodu: KYS-FR-12 · Revizyon No: 01 · Rev. Tarih: 01.11.2026" — this form's own identity. */
  const refLine = (doc: FormDocument) =>
    `${s(L.code)}: ${doc.ref.code} · ${s(L.revision)}: ${doc.ref.revision} · ${s(L.revisionDate)}: ${day(doc.ref.revisionDate)}`;

  form.documents.forEach((doc, i) => {
    const block: Content[] = [];
    // The form's own controlled-document identity (a period file may mix revisions).
    block.push({
      table: {
        widths: ["*", "auto", "auto"],
        body: [[
          { text: [{ text: `${s(L.code)}: `, color: MUTED }, { text: doc.ref.code, bold: true }] },
          { text: [{ text: `${s(L.revision)}: `, color: MUTED }, { text: doc.ref.revision, bold: true }] },
          { text: [{ text: `${s(L.revisionDate)}: `, color: MUTED }, { text: day(doc.ref.revisionDate), bold: true }] },
        ]],
      },
      layout: { ...grid, hLineColor: () => ACCENT },
      fontSize: 8,
      margin: [0, 0, 0, 4],
    } as Content);
    block.push({ text: s(doc.ref.title), bold: true, fontSize: 12, alignment: "center", margin: [0, 4, 0, 8] });
    if (meta.sample && meta.labels.sample) block.push({ text: s(meta.labels.sample), bold: true, fontSize: 8, color: "#8f5600", alignment: "center", margin: [0, -4, 0, 6] });
    if (doc.fields.length) {
      const rows: TableCell[][] = [];
      for (let k = 0; k < doc.fields.length; k += 2) {
        const a = doc.fields[k]!;
        const b = doc.fields[k + 1];
        rows.push([
          { text: s(a[0]), bold: true, fillColor: HEAD_FILL },
          s(a[1]),
          b ? { text: s(b[0]), bold: true, fillColor: HEAD_FILL } : "",
          b ? s(b[1]) : "",
        ]);
      }
      block.push({ table: { widths: ["auto", "*", "auto", "*"], body: rows }, layout: grid, fontSize: 8.5, margin: [0, 0, 0, 8] } as Content);
    }
    for (const p of doc.paragraphs ?? []) {
      block.push({ text: s(p.heading), bold: true, fontSize: 9, margin: [0, 2, 0, 1] });
      block.push({ text: s(p.text), fontSize: 8.5, margin: [0, 0, 0, 4] });
    }
    if (doc.table.heading) block.push({ text: s(doc.table.heading), bold: true, fontSize: 9, margin: [0, 4, 0, 3] });
    const t = doc.table;
    const docNo = doc.fields.find(([k]) => k === L.documentNo)?.[1];
    const body: TableCell[][] = [
      // Repeated on every page the table runs onto, so a continued page still names its form and revision.
      [
        { text: [refLine(doc), docNo ? ` · ${s(L.documentNo ?? "")}: ${docNo}` : ""].join(""), colSpan: t.columns.length, fontSize: 7, color: MUTED, border: [false, false, false, false] },
        ...t.columns.slice(1).map(() => ({ text: "", border: [false, false, false, false] })),
      ] as TableCell[],
      t.columns.map((c) => ({ text: s(c.header), bold: true, fillColor: HEAD_FILL, alignment: c.align ?? "left" })),
      ...t.rows.map((r) => t.columns.map((c, j) => ({ text: s(r[j] ?? ""), alignment: c.align ?? "left" }) as TableCell)),
    ];
    block.push({
      table: { headerRows: 2, keepWithHeaderRows: 1, dontBreakRows: true, widths: t.columns.map((c) => c.width ?? "auto"), body },
      // The identity row (row 0) is a caption: no rules above or beside it.
      layout: { ...grid, hLineWidth: (i: number) => (i === 0 ? 0 : 0.6), vLineWidth: (_i: number, node: unknown, row?: number) => (row === 0 ? 0 : 0.6) },
      fontSize: form.orientation === "landscape" ? 7.5 : 8.5,
      margin: [0, 0, 0, 10],
    } as Content);
    if (doc.note) block.push({ text: s(doc.note), italics: true, color: MUTED, fontSize: 7.5, margin: [0, -4, 0, 8] });
    if (doc.signatures.length) {
      block.push({
        table: {
          widths: doc.signatures.map(() => "*"),
          body: [
            doc.signatures.map(([label]) => ({ text: s(label), bold: true, alignment: "center", fillColor: HEAD_FILL }) as TableCell),
            doc.signatures.map(([, value]) => ({ text: s(value), alignment: "center", margin: [0, 8, 0, 8] }) as TableCell),
          ],
          dontBreakRows: true,
        },
        layout: grid,
        fontSize: 8.5,
        unbreakable: true,
      } as Content);
    }
    content.push({ stack: block, ...(i > 0 ? { pageBreak: "before" as const } : {}) } as Content);
  });
  if (form.documents.length === 0) content.push({ text: s(meta.labels.noRows), italics: true, margin: [0, 20, 0, 0] });

  return {
    pageSize: "A4",
    pageOrientation: form.orientation,
    compress,
    pageMargins: [MARGIN_X, 78, MARGIN_X, 48],
    info: { title: `${form.code} Rev.${form.revision} — ${meta.reportName}`, creator: "FTS", producer: "FTS" },
    // Page header: logo, report name and page. The code/revision/date belong to each FORM (block above its
    // fields and the repeated first table row), never to the page: a period file may mix revisions.
    header: (page?: number, pages?: number) => ({
      margin: [MARGIN_X, 20, MARGIN_X, 0],
      table: {
        widths: [110, "*", "auto"],
        body: [
          [
            logo ? { image: logo, width: 96, margin: [0, 2, 0, 2] } : { text: "FTS", bold: true, fontSize: 10 },
            { text: s(meta.reportName), bold: true, margin: [0, 8, 0, 0] },
            { text: fill(s(L.page), { page: page ?? 1, pages: pages ?? 1 }), margin: [0, 8, 0, 0] },
          ],
        ],
      },
      layout: { ...grid, hLineColor: () => ACCENT, vLineColor: () => LINE },
      fontSize: 8,
    }),
    footer: () => ({
      margin: [MARGIN_X, 10, MARGIN_X, 0],
      stack: [
        { text: s(L.controlled), fontSize: 7, color: MUTED, alignment: "center" },
        {
          columns: [
            { text: s(meta.labels.footer ?? ""), fontSize: 7, color: MUTED },
            {
              text: `${s(meta.labels.generatedAt)}: ${formatDateTimeIn(meta.generatedAt, meta.tz, meta.locale)} · ${s(meta.labels.generatedBy)}: ${s(meta.generatedByRole ?? meta.generatedBy)}`,
              fontSize: 7,
              color: MUTED,
              alignment: "right",
            },
          ],
          margin: [0, 2, 0, 0],
        },
      ],
    }),
    content,
    defaultStyle: { font: FONT, fontSize: 8.5, color: INK },
  } as TDocumentDefinitions;
}

export async function buildFormPdf(meta: ExportMeta, fonts: { regular: ArrayBuffer; bold: ArrayBuffer }, opts: { compress?: boolean; logo?: string | null } = {}): Promise<Uint8Array> {
  const pdfMake = await loadPdfMake();
  const vfs = { "NotoSans-Regular.ttf": toBase64(fonts.regular), "NotoSans-Bold.ttf": toBase64(fonts.bold) };
  const fontDefs = { [FONT]: { normal: "NotoSans-Regular.ttf", bold: "NotoSans-Bold.ttf", italics: "NotoSans-Regular.ttf", bolditalics: "NotoSans-Bold.ttf" } };
  const doc = pdfMake.createPdf(formPdfDefinition(meta, opts.logo, opts.compress ?? true), undefined, fontDefs, vfs);
  return new Promise<Uint8Array>((resolve, reject) => {
    try {
      doc.getBuffer((b) => resolve(b));
    } catch (e) {
      reject(e);
    }
  });
}
